/*
 * Copyright (c) 2025. Bolmate B.V.
 */
import './style/addNotification.scss';

import React, {ForwardedRef, forwardRef, useImperativeHandle, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {hasAdminLogin} from '../../../util/adminUtil';

import {NotificationType} from '../../../util/enums';

import {ActivateHandle} from '../../../util/interfaces';

import {AwesomeIcon, CloseButton, WithBackground} from '@dvrd/dvr-controls';

import {IconName} from '@fortawesome/fontawesome-svg-core';

import classNames from 'classnames';
import BolmateBadgePremium from '../bolmateBadgePremium';

import {BolmateBadge} from '../../controls';

interface Props {
    onChooseType: (notificationType: NotificationType) => void;
    moduleActive: boolean;
}

function AddNotification(props: Props, ref: ForwardedRef<ActivateHandle>) {
    const {t} = useTranslation();
    const {onChooseType, moduleActive} = props;
    const [active, setActive] = useState(false);

    function onActivate() {
        setActive(true);
    }

    function onClose() {
        setActive(false);
    }

    function onPick(notificationType: NotificationType) {
        return function () {
            onClose();
            onChooseType(notificationType);
        };
    }

    function renderBody() {
        return (
            <div className='body-container'>
                {renderType(NotificationType.REVIEWS, {premium: true})}
                {renderType(NotificationType.BUY_BOX, {premium: true})}
                {renderType(NotificationType.STOCK, {premium: true})}
                {renderType(NotificationType.PRODUCT_HIJACK, {premium: true})}
                {renderType(NotificationType.ORDER)}
            </div>
        );
    }

    function renderType(
        notificationType: NotificationType,
        config?: {soon?: boolean; premium?: boolean; beta?: boolean}
    ) {
        const soon = config?.soon;
        const premium = config?.premium;
        const disabled = soon || (premium && !moduleActive);
        let label: string;
        let description: string;
        let icon: IconName;
        let disabledText: string = '';
        if (notificationType === NotificationType.REVIEWS) {
            label = t('notifications.add.reviewLabel', 'Review notificatie');
            description = t('notifications.add.reviewDescription', 'Ontvang een e-mail bij nieuwe reviews');
            icon = 'star';
        } else if (notificationType === NotificationType.PRODUCT_HIJACK) {
            label = t('notifications.add.productHijackLabel', 'Productkaping notificatie');
            description = t(
                'notifications.add.productHijackDescription',
                'Ontvang een e-mail wanneer een concurrent ook als verkoper bij jouw product verschijnt.'
            );
            icon = 'user-shield';
        } else if (notificationType === NotificationType.BUY_BOX) {
            label = t('notifications.add.buyBoxLabel', 'Koopblok notificatie');
            description = t('notifications.add.buyBoxDescription', 'Ontvang een e-mail wanneer het koopblok verandert');
            icon = 'trophy';
        } else if (notificationType === NotificationType.STOCK) {
            label = t('notifications.add.stockLabel', 'Voorraad notificatie');
            description = t('notifications.add.stockDescription', 'Ontvang een e-mail als de voorraad bijna op is');
            icon = 'box-open';
        } else if (notificationType === NotificationType.ORDER) {
            label = t('notifications.add.orderLabel', 'Order notificatie');
            description = t('notifications.add.orderDescription', 'Ontvang een e-mail bij nieuwe orders');
            icon = 'truck-fast';
        } else return null;
        if (premium && !moduleActive) {
            disabledText = t('notifications.add.premiumBadge', 'Premium notificatie');
        } else if (soon) {
            disabledText = t('notifications.add.comingSoon', 'Binnenkort beschikbaar!');
        }
        const onClick = !soon || hasAdminLogin() ? onPick(notificationType) : undefined;
        return (
            <div className={classNames('item-picker', disabled && 'disabled')} onClick={onClick}>
                <AwesomeIcon name={icon} className='type-icon' />
                <div className='texts-container'>
                    <label className='item-picker-title'>
                        {label} {premium && <BolmateBadgePremium />}{' '}
                        {config?.beta && <BolmateBadge label={t('notifications.add.betaBadge', 'Beta')} />}
                    </label>
                    <label className='description'>{description}</label>
                </div>
                {disabledText && (
                    <div className='disabled-container'>
                        <label className='disabled-label'>
                            <AwesomeIcon name={icon} /> {disabledText}
                        </label>
                    </div>
                )}
            </div>
        );
    }

    useImperativeHandle(ref, () => ({activate: onActivate}));

    return (
        <WithBackground active={active} onClose={onClose}>
            <div className='default-popup add-notification'>
                <div className='header-container'>
                    <label className='header-title'>
                        {t('notifications.add.headerTitle', 'Notificatie toevoegen')}
                    </label>
                    <CloseButton onClick={onClose} />
                </div>
                {renderBody()}
            </div>
        </WithBackground>
    );
}

export default forwardRef<ActivateHandle, Props>(AddNotification);
